import React from "react";
import { cn } from "./ui/utils";
import { X } from "lucide-react";

interface ChipProps {
  label: string;
  selected?: boolean;
  removable?: boolean;
  icon?: React.ReactNode;
  color?: "default" | "indigo" | "emerald" | "amber" | "pink";
  size?: "sm" | "md";
  onClick?: () => void;
  onRemove?: () => void;
  className?: string;
}

const colorMap = {
  default: {
    idle: "bg-gray-100 text-gray-600 border-gray-200",
    active: "bg-gray-800 text-white border-gray-800",
  },
  indigo: {
    idle: "bg-indigo-50 text-indigo-600 border-indigo-200",
    active: "bg-indigo-500 text-white border-indigo-500",
  },
  emerald: {
    idle: "bg-emerald-50 text-emerald-600 border-emerald-200",
    active: "bg-emerald-500 text-white border-emerald-500",
  },
  amber: {
    idle: "bg-amber-50 text-amber-600 border-amber-200",
    active: "bg-amber-500 text-white border-amber-500",
  },
  pink: {
    idle: "bg-pink-50 text-pink-600 border-pink-200",
    active: "bg-pink-500 text-white border-pink-500",
  },
};

export function Chip({
  label,
  selected,
  removable,
  icon,
  color = "default",
  size = "md",
  onClick,
  onRemove,
  className,
}: ChipProps) {
  const colors = colorMap[color];
  const state = selected ? colors.active : colors.idle;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 border rounded-full transition-all duration-200 whitespace-nowrap flex-shrink-0",
        state,
        size === "sm" && "px-2 py-0.5 text-[11px]",
        size === "md" && "px-3 py-1 text-[12px]",
        onClick && "cursor-pointer active:scale-95",
        !onClick && "cursor-default",
        className
      )}
    >
      {icon}
      {label}
      {removable && onRemove && (
        <span
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 rounded-full hover:bg-black/10 p-0.5"
        >
          <X size={10} />
        </span>
      )}
    </button>
  );
}
